import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Tag, Layers, Plus, Edit, Trash2, Target, Settings, Zap, X, Save, ImagePlus, Filter, Star, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { BASE_URL, cleanHtmlContent, processHtmlAndUploadImages, createQuillImageHandler } from '../../utils/helpers';
import { toast } from 'react-toastify';
import ReactQuill, {Quill} from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const icons = Quill.import('ui/icons');
icons['unlink'] = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" x2="16" y1="12" y2="12"/><line x1="2" x2="22" y1="2" y2="22"/></svg>`;
const SearchInput = ({ value, onChange, placeholder }) => (
    <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-tnc-orange bg-gray-50 transition-colors"
        />
        {value && (
            <button onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={13} />
            </button>
        )}
    </div>
);

const AdminAttributes = () => {
    const [activeTab, setActiveTab] = useState('brand_cat');

    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [purposes, setPurposes] = useState([]);
    const [specs, setSpecs] = useState([]);
    const [sales, setSales] = useState([]);

    const [newBrand, setNewBrand] = useState({ name: "", file: null, preview: "" });
    const [editingBrandId, setEditingBrandId] = useState(null);
    const [isSavingBrand, setIsSavingBrand] = useState(false);

    const [newCategory, setNewCategory] = useState("");
    const [editingCatId, setEditingCatId] = useState(null);

    const [newPurpose, setNewPurpose] = useState("");
    const [editingPurposeId, setEditingPurposeId] = useState(null);

    const [newSpec, setNewSpec] = useState({ name: "", categoryId: "" });
    const [editingSpecId, setEditingSpecId] = useState(null);
    const [editingSpecName, setEditingSpecName] = useState("");

    const [editingSaleId, setEditingSaleId] = useState(null);
    const [newSale, setNewSale] = useState({
        saleName: "", description: "", isHidden: false, isFeatured: false, saleStartDate: "", saleEndDate: ""
    });

    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedPurpose, setDraggedPurpose] = useState(null);

    // ── Search states ──
    const [searchBrand, setSearchBrand] = useState("");
    const [searchCat, setSearchCat] = useState("");
    const [searchPurpose, setSearchPurpose] = useState("");

    // ── Pagination states for Sales ──
    const [saleCurrentPage, setSaleCurrentPage] = useState(1);
    const saleItemsPerPage = 9; // Hiển thị 9 Khuyến mãi 1 trang (3 hàng x 3 cột)
    const [saleJumpPage, setSaleJumpPage] = useState('');

    const saleQuillRef = useRef(null);

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['link', 'unlink', 'image'],
                ['clean']
            ],
            handlers: {
                unlink: function() { this.quill.format('link', false); },
                image: createQuillImageHandler(saleQuillRef, adminStaffService.uploadEditorImage, 'image_sales')
            }
        },
        keyboard: {
            bindings: {
                tab: {
                    key: 9,
                    handler: function(range, context) {
                        if (context.format.list) return true;
                        this.quill.insertText(range.index, '    ');
                        this.quill.setSelection(range.index + 4);
                        return false;
                    }
                }
            }
        },
        clipboard: { matchVisual: false }
    }), []);

    // ── Filtered lists ──
    const filteredBrands = useMemo(() => {
        const q = searchBrand.toLowerCase().trim();
        return q ? brands.filter(b => b.brandName.toLowerCase().includes(q)) : brands;
    }, [brands, searchBrand]);

    const filteredCategories = useMemo(() => {
        const q = searchCat.toLowerCase().trim();
        return q ? categories.filter(c => c.deviceCategoryName.toLowerCase().includes(q)) : categories;
    }, [categories, searchCat]);

    const filteredPurposes = useMemo(() => {
        const q = searchPurpose.toLowerCase().trim();
        return q ? purposes.filter(p => p.purposeName.toLowerCase().includes(q)) : purposes;
    }, [purposes, searchPurpose]);

    // ── Pagination Logic for Sales ──
    const totalSalePages = Math.ceil(sales.length / saleItemsPerPage) || 1;
    const validSaleCurrentPage = Math.min(saleCurrentPage, totalSalePages);
    const paginatedSales = sales.slice((validSaleCurrentPage - 1) * saleItemsPerPage, validSaleCurrentPage * saleItemsPerPage);

    const handleSalePageChange = (page) => {
        if (page >= 1 && page <= totalSalePages) {
            setSaleCurrentPage(page);
        }
    };

    const handleSaleJumpPage = () => {
        const page = parseInt(saleJumpPage, 10);
        if (!isNaN(page) && page >= 1 && page <= totalSalePages) {
            handleSalePageChange(page);
            setSaleJumpPage('');
        } else {
            setSaleJumpPage('');
        }
    };

    // ── Reorder helpers ──
    const handleManualReorder = (item, type, newPosition, currentArray, categoryId = null) => {
        if (!newPosition || isNaN(newPosition)) return;
        const parsedPos = parseInt(newPosition);
        if (parsedPos <= 0) return;
        const targetIndex = Math.max(0, Math.min(parsedPos - 1, currentArray.length - 1));
        const currentIndex = currentArray.findIndex(i =>
            type === 'purpose' ? i.purposeId === item.purposeId : i.specId === item.specId
        );
        if (currentIndex === targetIndex || currentIndex === -1) return;
        const reordered = [...currentArray];
        const [movedItem] = reordered.splice(currentIndex, 1);
        reordered.splice(targetIndex, 0, movedItem);
        if (type === 'purpose') handleReorderPurposes(reordered);
        else if (type === 'spec') handleReorderSpecs(categoryId, reordered);
    };

    const handleReorderSpecs = async (categoryId, newOrderedSpecs) => {
        const otherSpecs = specs.filter(s => s.deviceCategory?.deviceCategoryId !== categoryId);
        setSpecs([...otherSpecs, ...newOrderedSpecs]);
        try {
            await adminStaffService.reorderSpecs(newOrderedSpecs.map(s => s.specId));
            toast.success("Đã lưu thứ tự mới!");
        } catch {
            toast.error("Lỗi khi lưu thứ tự!");
            fetchData();
        }
    };

    const handleToggleSpecFilter = async (specId) => {
        try {
            const currentSpec = specs.find(s => s.specId === specId);
            const targetState = !currentSpec.isFilterable;
            setSpecs(prev => prev.map(s => s.specId === specId ? { ...s, isFilterable: targetState } : s));
            await adminStaffService.toggleSpecFilter(specId);
            toast.success(targetState ? "Đã BẬT hiển thị ở Bộ lọc" : "Đã TẮT hiển thị ở Bộ lọc");
        } catch {
            setSpecs(prev => prev.map(s => s.specId === specId ? { ...s, isFilterable: !s.isFilterable } : s));
            toast.error("Lỗi khi thay đổi trạng thái bộ lọc!");
        }
    };

    const onDragStart = (e, spec) => { setDraggedItem(spec); e.dataTransfer.effectAllowed = "move"; e.target.style.opacity = "0.4"; };
    const onDragEnd   = (e) => { e.target.style.opacity = "1"; setDraggedItem(null); };

    const handleDragOverContainer = (e) => {
        e.preventDefault();
        const container = document.getElementById("spec-scroll-container");
        if (!container || !draggedItem) return;
        const { top, bottom } = container.getBoundingClientRect();
        if (e.clientY < top + 60) container.scrollTop -= 12;
        else if (e.clientY > bottom - 60) container.scrollTop += 12;
    };

    const onDrop = (e, targetSpec, categoryId) => {
        e.preventDefault();
        if (!draggedItem || draggedItem.specId === targetSpec.specId) return;
        if (draggedItem.deviceCategory?.deviceCategoryId !== categoryId) {
            toast.warn("Chỉ có thể sắp xếp trong cùng danh mục!"); return;
        }
        const catSpecs = specs.filter(s => s.deviceCategory?.deviceCategoryId === categoryId);
        const reordered = [...catSpecs];
        const from = reordered.findIndex(s => s.specId === draggedItem.specId);
        const to   = reordered.findIndex(s => s.specId === targetSpec.specId);
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        handleReorderSpecs(categoryId, reordered);
    };

    const handleReorderPurposes = async (newOrderedPurposes) => {
        setPurposes(newOrderedPurposes);
        try {
            await adminStaffService.reorderPurposes(newOrderedPurposes.map(p => p.purposeId));
            toast.success("Đã lưu thứ tự Nhu cầu!");
        } catch {
            toast.error("Lỗi khi lưu thứ tự!");
            fetchData();
        }
    };

    const onDragStartPurpose = (e, purpose) => { setDraggedPurpose(purpose); e.dataTransfer.effectAllowed = "move"; e.target.style.opacity = "0.4"; };
    const onDragEndPurpose   = (e) => { e.target.style.opacity = "1"; setDraggedPurpose(null); };

    const handleDragOverPurposeContainer = (e) => {
        e.preventDefault();
        const container = document.getElementById("purpose-scroll-container");
        if (!container || !draggedPurpose) return;
        const { top, bottom } = container.getBoundingClientRect();
        if (e.clientY < top + 60) container.scrollTop -= 12;
        else if (e.clientY > bottom - 60) container.scrollTop += 12;
    };

    const onDropPurpose = (e, targetPurpose) => {
        e.preventDefault();
        if (!draggedPurpose || draggedPurpose.purposeId === targetPurpose.purposeId) return;
        const reordered = [...purposes];
        const from = reordered.findIndex(p => p.purposeId === draggedPurpose.purposeId);
        const to   = reordered.findIndex(p => p.purposeId === targetPurpose.purposeId);
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        handleReorderPurposes(reordered);
    };

    const fetchData = async () => {
        try {
            const [b, c, p, sp, sa] = await Promise.all([
                adminStaffService.getBrands().catch(()=>[]),
                adminStaffService.getCategories().catch(()=>[]),
                adminStaffService.getPurposes().catch(()=>[]),
                adminStaffService.getSpecs().catch(()=>[]),
                adminStaffService.getSales().catch(()=>[])
            ]);
            const safeArray = (data) => Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
            setBrands(safeArray(b));
            setCategories(safeArray(c));
            const salesData = safeArray(sa);
            salesData.sort((a, b) => (a.isFeatured && !b.isFeatured ? -1 : !a.isFeatured && b.isFeatured ? 1 : 0));
            setSales(salesData);
            const purposesData = safeArray(p);
            purposesData.sort((a, b) => (a.purposeOrder || 0) - (b.purposeOrder || 0));
            setPurposes(purposesData);
            const specsData = safeArray(sp);
            specsData.sort((a, b) => {
                const diff = (a.specOrder || 0) - (b.specOrder || 0);
                return diff !== 0 ? diff : (a.specId || 0) - (b.specId || 0);
            });
            setSpecs(specsData);
        } catch (error) { console.error(error); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleDelete = async (id, type) => {
        if (!window.confirm("Chắc chắn xóa mục này? Dữ liệu liên kết cũng sẽ bị xóa!")) return;
        try {
            if (type === 'brand')   await adminStaffService.deleteBrand(id);
            if (type === 'cat')     await adminStaffService.deleteCategory(id);
            if (type === 'purpose') await adminStaffService.deletePurpose(id);
            if (type === 'spec')    await adminStaffService.deleteSpec(id);
            if (type === 'sale')    {
                await adminStaffService.deleteSale(id);
                // Kiểm tra xem trang hiện tại có bị trống sau khi xóa không
                if (paginatedSales.length === 1 && saleCurrentPage > 1) {
                    setSaleCurrentPage(saleCurrentPage - 1);
                }
            }
            fetchData();
        } catch { toast("Lỗi khi xóa!"); }
    };

    const handleAdd = async (e, type) => {
        e.preventDefault();
        try {
            if (type === 'brand') {
                setIsSavingBrand(true);
                const toastId = toast.loading(editingBrandId ? "Đang cập nhật banner..." : "Đang thêm banner...");
                
                try {
                    const formData = new FormData();
                    formData.append("brandName", newBrand.name);
                    if (newBrand.file) formData.append("image", newBrand.file);
                    
                    if (editingBrandId) { 
                        await adminStaffService.updateBrand(editingBrandId, formData); 
                        setEditingBrandId(null); 
                    } else { 
                        await adminStaffService.createBrand(formData); 
                    }
                    
                    setNewBrand({ name: "", file: null, preview: "" });
                    toast.update(toastId, { 
                        render: editingBrandId ? "Cập nhật banner thành công!" : "Thêm banner thành công!", 
                        type: "success", 
                        isLoading: false, 
                        autoClose: 2000 
                    });
                    fetchData();
                } catch (error) {
                    toast.update(toastId, { render: "Lỗi khi lưu banner!", type: "error", isLoading: false, autoClose: 3000 });
                } finally {
                    setIsSavingBrand(false);
                }
                return;
            }
            if (type === 'cat') {
                if (editingCatId) { await adminStaffService.updateCategory(editingCatId, { deviceCategoryName: newCategory }); setEditingCatId(null); }
                else { await adminStaffService.createCategory({ deviceCategoryName: newCategory }); }
                setNewCategory("");
            }
            if (type === 'purpose') {
                if (editingPurposeId) { await adminStaffService.updatePurpose(editingPurposeId, { purposeName: newPurpose }); setEditingPurposeId(null); }
                else { await adminStaffService.createPurpose({ purposeName: newPurpose }); }
                setNewPurpose("");
            }
            if (type === 'spec') {
                const catId = newSpec.categoryId;
                if (!catId) { toast.warn("Vui lòng chọn danh mục!"); return; }
                const inputLines = newSpec.name.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                if (inputLines.length === 0) { toast.warn("Vui lòng nhập tên thông số!"); return; }
                const uniqueLines = [...new Set(inputLines)];
                const existingNames = specs.filter(s => s.deviceCategory?.deviceCategoryId === parseInt(catId)).map(s => s.specName.toLowerCase().trim());
                const toAdd = []; let skipped = 0;
                uniqueLines.forEach(line => existingNames.includes(line.toLowerCase()) ? skipped++ : toAdd.push(line));
                if (toAdd.length === 0) { toast.warn(`Đã bỏ qua ${skipped} thông số do trùng lặp.`); return; }
                for (const specName of toAdd) await adminStaffService.createSpec({ specName, categoryId: catId });
                toast.success(skipped > 0 ? `Đã thêm ${toAdd.length} thông số. Bỏ qua ${skipped} trùng!` : `Đã thêm ${toAdd.length} thông số!`);
                setNewSpec({ name: "", categoryId: catId });
                fetchData(); return;
            }
            if (type === 'sale') {
                const toastId = toast.loading("Đang xử lý ảnh và lưu khuyến mãi...");
                try {
                    const cleanDescription = await processHtmlAndUploadImages(newSale.description, 'image_sale', adminStaffService.uploadEditorImage, adminStaffService.uploadExternalEditorImage);
                    const payload = { ...newSale, description: cleanHtmlContent(cleanDescription), saleStartDate: newSale.saleStartDate || null, saleEndDate: newSale.saleEndDate || null };
                    if (editingSaleId) { 
                        await adminStaffService.updateSale(editingSaleId, payload); 
                        setEditingSaleId(null); 
                    } else { 
                        await adminStaffService.createSale(payload); 
                        setSaleCurrentPage(1); // Chuyển về trang đầu khi thêm mới
                    }
                    setNewSale({ saleName: "", description: "", isHidden: false, isFeatured: false, saleStartDate: "", saleEndDate: "" });
                    toast.update(toastId, { render: "Đã lưu khuyến mãi thành công!", type: "success", isLoading: false, autoClose: 2000 });
                    fetchData(); return;
                } catch { toast.update(toastId, { render: "Lỗi khi lưu khuyến mãi!", type: "error", isLoading: false, autoClose: 3000 }); return; }
            }
            fetchData();
        } catch { toast("Lỗi khi lưu dữ liệu!"); }
    };

    const handleEditBrand   = (b) => { setEditingBrandId(b.brandId); setNewBrand({ name: b.brandName, file: null, preview: b.brandImageUrl ? `${BASE_URL}${b.brandImageUrl}` : "" }); };
    const handleEditCat     = (c) => { setEditingCatId(c.deviceCategoryId); setNewCategory(c.deviceCategoryName); };
    const handleEditPurpose = (p) => { setEditingPurposeId(p.purposeId); setNewPurpose(p.purposeName); };
    const handleEditSpecStart  = (s) => { setEditingSpecId(s.specId); setEditingSpecName(s.specName); };
    const handleEditSpecCancel = () => { setEditingSpecId(null); setEditingSpecName(""); };

    const handleEditSpecSave = async (specId) => {
        if (!editingSpecName.trim()) { toast.warn("Tên thông số không được để trống!"); return; }
        try {
            await adminStaffService.updateSpec(specId, { specName: editingSpecName.trim() });
            toast.success("Cập nhật tên thông số thành công!");
            setEditingSpecId(null); setEditingSpecName(""); fetchData();
        } catch { toast.error("Lỗi khi cập nhật tên thông số!"); }
    };

    const handleEditSale = (s) => {
        setEditingSaleId(s.saleId);
        setNewSale({ saleName: s.saleName || "", description: s.description || "", isHidden: s.isHidden || false, isFeatured: s.isFeatured || false, saleStartDate: s.saleStartDate ? s.saleStartDate.substring(0, 16) : "", saleEndDate: s.saleEndDate ? s.saleEndDate.substring(0, 16) : "" });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatDate = (d) => d ? new Date(d).toLocaleString('vi-VN') : "Không giới hạn";

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <style dangerouslySetInnerHTML={{__html: `
                .ql-editor { min-height: 150px; font-family: inherit; font-size: 14px; font-weight: 400; color: #374151; }
                .ql-editor strong, .ql-editor b { font-weight: 700; }
                .ql-snow .ql-toolbar button.ql-unlink svg { width: 18px; height: 18px; }
                .ql-snow .ql-toolbar button.ql-unlink { color: #444; }
                .ql-snow .ql-toolbar button.ql-unlink:hover { color: #06c; }
            `}} />
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Quản lý Thuộc tính & Khuyến mãi</h2>

            <div className="flex gap-4 border-b border-gray-200 mb-6">
                <button onClick={() => setActiveTab('brand_cat')} className={`pb-3 font-bold px-4 border-b-2 transition-colors ${activeTab === 'brand_cat' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-tnc-orange'}`}>Hãng & Danh mục</button>
                <button onClick={() => setActiveTab('spec_purpose')} className={`pb-3 font-bold px-4 border-b-2 transition-colors ${activeTab === 'spec_purpose' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-tnc-orange'}`}>Thông số & Nhu cầu</button>
                <button onClick={() => setActiveTab('sale')} className={`pb-3 font-bold px-4 border-b-2 transition-colors ${activeTab === 'sale' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-tnc-orange'}`}>Khuyến mãi (Sale)</button>
            </div>

            {/* TAB 1: BRAND & CAT */}
            {activeTab === 'brand_cat' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* BRAND */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-bold flex items-center gap-2 mb-4 text-gray-800"><Tag size={20} className="text-tnc-orange"/> Quản lý Hãng (Brand)</h3>
                        <form onSubmit={(e) => handleAdd(e, 'brand')} className={`flex flex-col gap-3 mb-4 p-4 rounded-xl border ${editingBrandId ? 'bg-orange-50 border-orange-200 shadow-sm' : 'bg-gray-50 border-gray-100'}`}>
                            <div className="flex gap-2 items-center">
                                <input type="text" value={newBrand.name} onChange={e => setNewBrand(prev => ({...prev, name: e.target.value}))} placeholder="Tên hãng..." className="flex-1 border rounded px-3 py-2 outline-none focus:border-tnc-orange" required/>
                                <label className="bg-white border border-gray-300 text-gray-600 px-3 py-2 rounded cursor-pointer hover:bg-gray-100 hover:text-tnc-orange flex items-center justify-center transition-colors" title="Thêm Logo">
                                    <ImagePlus size={18} />
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files[0]; if(file) setNewBrand(prev => ({...prev, file, preview: URL.createObjectURL(file)})); }}/>
                                </label>
                               {editingBrandId ? (
                                    <>
                                        <button 
                                            type="submit" 
                                            disabled={isSavingBrand}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded transition-colors disabled:opacity-50 flex items-center justify-center min-w-[42px]"
                                        >
                                            {isSavingBrand ? <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> : <Save size={18}/>}
                                        </button>
                                        <button type="button" disabled={isSavingBrand} onClick={() => {setEditingBrandId(null); setNewBrand({name:"", file:null, preview:""});}} className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-2 rounded transition-colors disabled:opacity-50"><X size={18}/></button>
                                    </>
                                ) : (
                                    <button 
                                        type="submit"
                                        disabled={isSavingBrand} 
                                        className="bg-tnc-orange hover:bg-tnc-orange-hover text-white px-3 py-2 rounded transition-colors disabled:opacity-50 flex items-center justify-center min-w-[42px]"
                                    >
                                        {isSavingBrand ? <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> : <Plus size={18}/>}
                                    </button>
                                )}
                            </div>
                            {newBrand.preview && (
                                <div className="relative w-24 h-16 border border-gray-200 rounded-lg bg-white p-1 shadow-sm mt-1">
                                    <img src={newBrand.preview} alt="preview" className="w-full h-full object-contain" />
                                    <button type="button" onClick={() => setNewBrand(prev => ({...prev, file: null, preview: ""}))} className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"><X size={12}/></button>
                                </div>
                            )}
                        </form>

                        {/* Search brand */}
                        <SearchInput value={searchBrand} onChange={setSearchBrand} placeholder={`Tìm trong ${brands.length} hãng...`} />

                        {filteredBrands.length === 0 && (
                            <p className="text-center text-sm text-gray-400 py-4">Không tìm thấy hãng nào.</p>
                        )}
                        <ul className="space-y-2 max-h-[460px] overflow-y-auto pr-2">
                            {filteredBrands.map(b => (
                                <li key={b.brandId} className={`flex items-center justify-between p-3 border rounded-xl text-sm font-medium transition-all ${editingBrandId === b.brandId ? 'bg-orange-50 border-orange-200' : 'bg-white hover:border-tnc-orange'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-10 bg-white border border-gray-100 rounded flex items-center justify-center overflow-hidden p-1 shadow-sm">
                                            {b.brandImageUrl ? (
                                                <img src={`${BASE_URL}${b.brandImageUrl}`} alt={b.brandName} className="max-w-full max-h-full object-contain" />
                                            ) : (
                                                <span className="text-[9px] text-gray-400">No img</span>
                                            )}
                                        </div>
                                        <span className="text-gray-800">{b.brandName}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleEditBrand(b)} className="p-1.5 bg-blue-50 text-blue-500 rounded hover:bg-blue-600 hover:text-white transition-colors"><Edit size={16}/></button>
                                        <button onClick={() => handleDelete(b.brandId, 'brand')} className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-600 hover:text-white transition-colors"><Trash2 size={16}/></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* CATEGORY */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-bold flex items-center gap-2 mb-4 text-gray-800"><Layers size={20} className="text-tnc-orange"/> Danh mục Thiết bị</h3>
                        <form onSubmit={(e) => handleAdd(e, 'cat')} className={`flex gap-2 mb-4 p-2 rounded border ${editingCatId ? 'bg-orange-50 border-orange-200' : 'border-transparent'}`}>
                            <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Tên danh mục..." className="flex-1 border rounded px-3 py-2 outline-none focus:border-tnc-orange" required/>
                            {editingCatId ? (
                                <>
                                    <button type="submit" className="bg-blue-600 text-white px-3 py-2 rounded"><Save size={18}/></button>
                                    <button type="button" onClick={() => {setEditingCatId(null); setNewCategory("");}} className="bg-gray-400 text-white px-3 py-2 rounded"><X size={18}/></button>
                                </>
                            ) : (
                                <button className="bg-tnc-orange hover:bg-tnc-orange-hover text-white px-3 py-2 rounded transition-colors"><Plus size={18}/></button>
                            )}
                        </form>

                        {/* Search category */}
                        <SearchInput value={searchCat} onChange={setSearchCat} placeholder={`Tìm trong ${categories.length} danh mục...`} />

                        {filteredCategories.length === 0 && (
                            <p className="text-center text-sm text-gray-400 py-4">Không tìm thấy danh mục nào.</p>
                        )}
                        <ul className="space-y-2 max-h-[460px] overflow-y-auto pr-2">
                            {filteredCategories.map(c => (
                                <li key={c.deviceCategoryId} className={`flex justify-between p-3 border rounded text-sm font-medium ${editingCatId === c.deviceCategoryId ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 hover:border-tnc-orange hover:bg-white border-gray-100 transition-colors'}`}>
                                    <span>{c.deviceCategoryName}</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleEditCat(c)} className="text-blue-500 hover:text-blue-700"><Edit size={16}/></button>
                                        <button onClick={() => handleDelete(c.deviceCategoryId, 'cat')} className="text-red-500 hover:text-red-700"><Trash2 size={16}/></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* TAB 2: SPEC & PURPOSE */}
            {activeTab === 'spec_purpose' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* PURPOSE */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2"><Target size={20} className="text-green-500"/> Nhu cầu sử dụng (Purpose)</h3>
                            <span className="text-[10px] text-gray-400">Kéo thả hoặc Nhập số</span>
                        </div>
                        <form onSubmit={(e) => handleAdd(e, 'purpose')} className={`flex gap-2 mb-3 p-2 rounded border ${editingPurposeId ? 'bg-blue-50 border-blue-200' : 'border-transparent'}`}>
                            <input type="text" value={newPurpose} onChange={e => setNewPurpose(e.target.value)} placeholder="Văn phòng, Chơi game..." className="flex-1 border rounded px-3 py-2 outline-none focus:border-tnc-orange" required/>
                            {editingPurposeId ? (
                                <>
                                    <button type="submit" className="bg-blue-600 text-white px-3 py-2 rounded"><Save size={18}/></button>
                                    <button type="button" onClick={() => {setEditingPurposeId(null); setNewPurpose("");}} className="bg-gray-400 text-white px-3 py-2 rounded"><X size={18}/></button>
                                </>
                            ) : (
                                <button className="bg-green-600 text-white px-3 py-2 rounded"><Plus size={18}/></button>
                            )}
                        </form>

                        {/* Search purpose */}
                        <SearchInput value={searchPurpose} onChange={setSearchPurpose} placeholder={`Tìm trong ${purposes.length} nhu cầu...`} />

                        {filteredPurposes.length === 0 && (
                            <p className="text-center text-sm text-gray-400 py-4">Không tìm thấy nhu cầu nào.</p>
                        )}

                        <ul
                            id="purpose-scroll-container"
                            onDragOver={handleDragOverPurposeContainer}
                            className="space-y-2 max-h-96 overflow-y-auto pr-2 scroll-smooth"
                        >
                            {/* Khi search: chỉ hiển thị, không có số thứ tự kéo thả */}
                            {searchPurpose.trim() ? (
                                filteredPurposes.map(p => (
                                    <li key={p.purposeId} className={`flex justify-between items-center p-3 border rounded text-sm font-medium transition-all ${editingPurposeId === p.purposeId ? 'bg-blue-50 border-blue-200' : 'bg-green-50/30 border-green-100 hover:border-green-300 shadow-sm'}`}>
                                        <span className="truncate">{p.purposeName}</span>
                                        <div className="flex gap-2 shrink-0 ml-2">
                                            <button onClick={() => handleEditPurpose(p)} className="text-blue-500 hover:text-blue-700 p-1"><Edit size={16}/></button>
                                            <button onClick={() => handleDelete(p.purposeId, 'purpose')} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16}/></button>
                                        </div>
                                    </li>
                                ))
                            ) : (
                                purposes.map((p, index) => (
                                    <li
                                        key={p.purposeId}
                                        draggable
                                        onDragStart={(e) => onDragStartPurpose(e, p)}
                                        onDragEnd={onDragEndPurpose}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => onDropPurpose(e, p)}
                                        className={`flex justify-between items-center p-3 border rounded text-sm font-medium transition-all cursor-grab active:cursor-grabbing
                                            ${draggedPurpose?.purposeId === p.purposeId
                                                ? 'bg-green-100 border-green-500 opacity-50'
                                                : editingPurposeId === p.purposeId
                                                    ? 'bg-blue-50 border-blue-200'
                                                    : 'bg-green-50/30 border-green-100 hover:border-green-300 shadow-sm'}`}
                                    >
                                        <div className="flex items-center gap-3 select-none truncate w-full">
                                            <div className="flex items-center gap-1.5 shrink-0" onDragStart={(e) => e.stopPropagation()}>
                                                <Target size={14} className="text-gray-300" title="Kéo để sắp xếp" />
                                                <input
                                                    key={`input-purpose-${p.purposeId}-${index}`}
                                                    type="number" min="1" max={purposes.length} defaultValue={index + 1}
                                                    onBlur={(e) => handleManualReorder(p, 'purpose', e.target.value, purposes)}
                                                    onKeyDown={(e) => { if(e.key === 'Enter') e.target.blur(); }}
                                                    className="w-10 h-7 text-center text-xs font-bold border border-gray-200 rounded outline-none focus:border-green-500 cursor-text"
                                                    title="Nhập số và nhấn Enter"
                                                />
                                            </div>
                                            <span className="truncate">{p.purposeName}</span>
                                        </div>
                                        <div className="flex gap-2 shrink-0 ml-2">
                                            <button onClick={() => handleEditPurpose(p)} className="text-blue-500 hover:text-blue-700 p-1"><Edit size={16}/></button>
                                            <button onClick={() => handleDelete(p.purposeId, 'purpose')} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16}/></button>
                                        </div>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>

                    {/* SPEC */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2"><Settings size={20} className="text-tnc-orange"/> Cấu hình Preset (Specs)</h3>
                            <span className="text-[10px] text-gray-400">Kéo thả hoặc Nhập số</span>
                        </div>
                        <form onSubmit={(e) => handleAdd(e, 'spec')} className="flex flex-col gap-2 mb-6">
                            <select value={newSpec.categoryId} onChange={e => setNewSpec(prev => ({...prev, categoryId: e.target.value}))} className="border rounded px-3 py-2 outline-none focus:border-tnc-orange text-sm" required>
                                <option value="">-- Thuộc Danh mục nào? --</option>
                                {categories.map(c => (<option key={c.deviceCategoryId} value={c.deviceCategoryId}>{c.deviceCategoryName}</option>))}
                            </select>
                            <div className="flex gap-2 items-start">
                                <textarea value={newSpec.name} onChange={e => setNewSpec(prev => ({...prev, name: e.target.value}))} placeholder="Nhập tên thông số (Mỗi dòng 1 thông số)&#10;CPU&#10;RAM&#10;VGA..." className="flex-1 border rounded px-3 py-2 outline-none focus:border-tnc-orange text-sm min-h-[80px] resize-y" required/>
                                <button className="bg-tnc-orange hover:bg-tnc-orange-hover text-white px-3 py-2 rounded h-[80px] flex items-center justify-center transition-colors"><Plus size={18}/></button>
                            </div>
                        </form>

                        <div id="spec-scroll-container" onDragOver={handleDragOverContainer} className="space-y-4 max-h-[500px] overflow-y-auto pr-2 scroll-smooth">
                            {!newSpec.categoryId ? (
                                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                    <p className="text-sm text-gray-500">Vui lòng chọn danh mục ở phía trên để xem và quản lý thông số.</p>
                                </div>
                            ) : (
                                categories.filter(cat => cat.deviceCategoryId.toString() === newSpec.categoryId.toString()).map(cat => {
                                    const catSpecs = specs.filter(s => s.deviceCategory?.deviceCategoryId === cat.deviceCategoryId);
                                    if (catSpecs.length === 0) return (
                                        <div key={cat.deviceCategoryId} className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                            <p className="text-sm text-gray-500">Danh mục này chưa có thông số nào.</p>
                                        </div>
                                    );
                                    return (
                                        <div key={cat.deviceCategoryId} className="mb-4 bg-gray-50/50 rounded-lg p-2 border border-gray-100">
                                            <h4 className="font-bold text-[10px] text-blue-600 uppercase mb-2 px-1 flex justify-between">
                                                <span>{cat.deviceCategoryName}</span>
                                                <span className="text-[9px] text-gray-400 normal-case">Kéo hoặc Nhập số</span>
                                            </h4>
                                            <ul className="space-y-1.5">
                                                {catSpecs.map((s, index) => (
                                                    <li key={s.specId} draggable
                                                        onDragStart={(e) => onDragStart(e, s)} onDragEnd={onDragEnd}
                                                        onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, s, cat.deviceCategoryId)}
                                                        className={`flex justify-between items-center py-1.5 px-2 border rounded-md text-[13px] transition-all ${draggedItem?.specId === s.specId ? 'bg-blue-100 border-blue-500 opacity-50' : 'bg-white border-gray-200 hover:bg-orange-50 hover:border-tnc-orange shadow-sm'} cursor-grab active:cursor-grabbing`}
                                                    >
                                                        <div className="flex items-center gap-3 select-none truncate w-full">
                                                            <div className="flex items-center gap-1.5 shrink-0" onDragStart={(e) => e.stopPropagation()}>
                                                                <Layers size={14} className="text-gray-300" title="Kéo để sắp xếp" />
                                                                <input key={`input-spec-${s.specId}-${index}`} type="number" min="1" max={catSpecs.length} defaultValue={index + 1}
                                                                    onBlur={(e) => handleManualReorder(s, 'spec', e.target.value, catSpecs, cat.deviceCategoryId)}
                                                                    onKeyDown={(e) => { if(e.key === 'Enter') e.target.blur(); }}
                                                                    className="w-10 h-7 text-center text-xs font-bold border border-gray-200 rounded outline-none focus:border-tnc-orange cursor-text" title="Nhập số và nhấn Enter"
                                                                />
                                                            </div>
                                                            {editingSpecId === s.specId ? (
                                                                <input type="text" value={editingSpecName} onChange={(e) => setEditingSpecName(e.target.value)}
                                                                    className="flex-1 border border-blue-300 rounded px-2 py-0.5 text-[13px] font-medium outline-none focus:ring-2 focus:ring-blue-200" autoFocus
                                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleEditSpecSave(s.specId); }}
                                                                />
                                                            ) : (
                                                                <span className="text-gray-700 truncate font-medium">{s.specName}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1 shrink-0 ml-2">
                                                            {editingSpecId === s.specId ? (
                                                                <>
                                                                    <button onClick={() => handleEditSpecSave(s.specId)} className="p-1 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors" title="Lưu"><Save size={14}/></button>
                                                                    <button onClick={handleEditSpecCancel} className="p-1 text-gray-500 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors" title="Hủy"><X size={14}/></button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button onClick={() => handleEditSpecStart(s)} className="p-1 text-blue-400 hover:text-blue-600 bg-blue-50/50 hover:bg-blue-100 rounded-md transition-colors border border-transparent hover:border-blue-200" title="Sửa tên thông số"><Edit size={14}/></button>
                                                                    <button onClick={() => handleToggleSpecFilter(s.specId)} className={`p-1 rounded-md border transition-colors ${s.isFilterable ? 'bg-orange-50 text-tnc-orange border-orange-200 hover:bg-orange-100' : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-200 hover:text-gray-600'}`} title={s.isFilterable ? "Đang hiển thị ở Bộ Lọc" : "Đã ẩn khỏi Bộ Lọc"}>
                                                                        <Filter size={14} className={s.isFilterable ? "fill-current" : ""}/>
                                                                    </button>
                                                                    <button onClick={() => handleDelete(s.specId, 'spec')} className="p-1 text-red-400 hover:text-red-600 bg-red-50/50 hover:bg-red-100 rounded-md transition-colors border border-transparent hover:border-red-200 ml-1" title="Xóa thông số"><Trash2 size={14}/></button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: SALE */}
            {activeTab === 'sale' && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-bold flex items-center gap-2 mb-4 text-tnc-orange">
                        <Zap size={20} /> {editingSaleId ? 'Cập nhật Khuyến Mãi' : 'Tạo chương trình Khuyến Mãi'}
                    </h3>
                    <form onSubmit={(e) => handleAdd(e, 'sale')} className={`flex flex-col gap-4 p-5 rounded-xl border-2 mb-8 transition-colors ${editingSaleId ? 'bg-orange-50 border-orange-300 shadow-md' : 'bg-red-50/30 border-red-100'}`}>
                        <div>
                            <label className="block text-sm font-bold mb-1">Tên Khuyến Mãi *</label>
                            <input type="text" value={newSale.saleName} onChange={e => setNewSale(prev => ({...prev, saleName: e.target.value}))} required className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-tnc-orange font-bold text-tnc-orange" placeholder="VD: Tặng Balo..."/>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Từ ngày (Tùy chọn)</label>
                                <input type="datetime-local" value={newSale.saleStartDate} onChange={e => setNewSale(prev => ({...prev, saleStartDate: e.target.value}))} className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-tnc-orange"/>
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Đến ngày (Tùy chọn)</label>
                                <input type="datetime-local" value={newSale.saleEndDate} onChange={e => setNewSale(prev => ({...prev, saleEndDate: e.target.value}))} className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-tnc-orange"/>
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold mb-1 text-gray-700">Mô tả chi tiết</label>
                            <div className="bg-white border rounded-lg border-gray-300 focus-within:border-tnc-orange transition-colors">
                                <ReactQuill ref={saleQuillRef} theme="snow" value={newSale.description} onChange={val => setNewSale(prev => ({...prev, description: val}))} modules={quillModules} placeholder="Soạn thảo thông tin ưu đãi..." className="[&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-container]:border-none"/>
                            </div>
                        </div>
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-2 pt-4 border-t border-gray-100">
                            <div className="flex flex-col sm:flex-row gap-4 mb-4 md:mb-0">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={newSale.isHidden} onChange={e => setNewSale(prev => ({...prev, isHidden: e.target.checked}))} className="w-4 h-4 accent-tnc-orange" />
                                    <span className="text-sm font-medium text-gray-700">Tạm ẩn (Chưa kích hoạt)</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={newSale.isFeatured} onChange={e => setNewSale(prev => ({...prev, isFeatured: e.target.checked}))} className="w-4 h-4 text-tnc-orange accent-tnc-orange" />
                                    <span className="text-sm font-bold text-tnc-orange flex items-center gap-1"><Star size={14}/> Hiển thị lên trang Hot Deal</span>
                                </label>
                            </div>
                            <div className="flex gap-2 w-full md:w-auto">
                                {editingSaleId && <button type="button" onClick={() => {setEditingSaleId(null); setNewSale({ saleName: "", description: "", isHidden: false, isFeatured: false, saleStartDate: "", saleEndDate: "" });}} className="flex-1 md:flex-none bg-gray-400 text-white font-bold px-6 py-2 rounded hover:bg-gray-500">Hủy</button>}
                                <button type="submit" className="flex-1 md:flex-none bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold px-8 py-2 rounded shadow-md flex items-center justify-center gap-2 transition-colors">
                                    {editingSaleId ? <><Save size={18}/> Lưu Cập Nhật</> : <><Plus size={18}/> Tạo Khuyến Mãi</>}
                                </button>
                            </div>
                        </div>
                    </form>

                    <h4 className="font-bold text-gray-700 mb-3 border-b pb-2">Danh sách Khuyến mãi</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {paginatedSales.map(s => (
                            <div key={s.saleId} className={`p-4 border rounded-xl relative transition-all duration-300 flex flex-col ${s.isHidden ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-orange-200 shadow-sm'} ${editingSaleId === s.saleId ? 'ring-2 ring-tnc-orange scale-[1.02]' : ''}`}>
                                <h5 className="font-extrabold text-tnc-orange text-lg line-clamp-1 pr-12" title={s.saleName}>{s.saleName || 'Chưa có tên'}</h5>
                                {s.isFeatured && (
                                    <span className="inline-flex mt-1 items-center w-max gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-tnc-orange">
                                        <Star size={10} className="fill-tnc-orange"/> HOT DEAL
                                    </span>
                                )}
                                <div className="text-gray-700 text-[13px] font-normal mt-2 line-clamp-3 flex-grow break-words [&_img]:max-w-full [&_img]:h-auto [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_li]:mb-0.5 [&_p]:my-0.5 [&_strong]:font-bold [&_b]:font-bold [&_em]:italic" title="Mô tả">
                                    {s.description ? <div dangerouslySetInnerHTML={{ __html: cleanHtmlContent(s.description) }} /> : <span className="italic text-gray-400">Không có mô tả</span>}
                                </div>
                                <p className="text-[11px] text-gray-500 mt-3 p-2 bg-gray-50 rounded border border-gray-100 font-mono">
                                    <span className="text-green-600 font-bold">Từ:</span> {formatDate(s.saleStartDate)} <br/>
                                    <span className="text-red-500 font-bold">Đến:</span> {formatDate(s.saleEndDate)}
                                </p>
                                <div className="absolute top-3 right-3 flex gap-1">
                                    <button onClick={() => handleEditSale(s)} className="p-1.5 bg-blue-50 text-blue-500 rounded hover:bg-blue-600 hover:text-white transition-colors" title="Sửa"><Edit size={16}/></button>
                                    <button onClick={() => handleDelete(s.saleId, 'sale')} className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-600 hover:text-white transition-colors" title="Xóa"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* --- Khối điều khiển Phân Trang Khuyến Mãi --- */}
                    {totalSalePages > 1 && (
                        <div className="flex flex-wrap justify-center items-center gap-4 mt-6 p-4 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={() => handleSalePageChange(validSaleCurrentPage - 1)} 
                                    disabled={validSaleCurrentPage === 1}
                                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                >
                                    <ChevronLeft size={16} />
                                </button>

                                <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] scrollbar-hide">
                                    {[...Array(totalSalePages)].map((_, i) => {
                                        const page = i + 1;
                                        return (
                                            <button 
                                                key={page} 
                                                onClick={() => handleSalePageChange(page)}
                                                className={`w-8 h-8 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                    ${validSaleCurrentPage === page 
                                                        ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        )
                                    })}
                                </div>

                                <button 
                                    onClick={() => handleSalePageChange(validSaleCurrentPage + 1)} 
                                    disabled={validSaleCurrentPage === totalSalePages}
                                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>

                            <div className="flex items-center gap-2 border-l border-gray-300 pl-4">
                                <span className="text-sm text-gray-600 font-medium">Đến trang:</span>
                                <input
                                    type="number"
                                    min="1"
                                    max={totalSalePages}
                                    value={saleJumpPage}
                                    onChange={(e) => setSaleJumpPage(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSaleJumpPage()}
                                    placeholder={validSaleCurrentPage.toString()}
                                    className="w-14 h-8 px-2 text-center border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-gray-700 shadow-sm"
                                />
                                <button
                                    onClick={handleSaleJumpPage}
                                    className="px-3 h-8 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors shadow-sm"
                                >
                                    Đi
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminAttributes;